/**
 * useIvyPageContext — derives a typed PageContext from the current route.
 *
 * Returns a stable PageContext object that IvyChat (via IvyDock) passes to
 * /api/ivy/chat on every turn.
 *
 * Phase 2D caller location: IvyDock is portal-mounted at App root, OUTSIDE
 * the <Routes> subtree. useParams() would return {} in that position because
 * there's no ancestor <Route> match. Therefore we extract route params by
 * regex over pathname (useLocation works from anywhere inside <BrowserRouter>)
 * — this is the portable form.
 *
 * Phase 2B: `/brain/plan/:id` → `focusedPlannedEpisodeId` (route-derived).
 * Phase 2C: state-based focus is picked up via PageFocusContext (usePageFocus).
 *   Pages publish entity focus via useRegisterPageFocus; this hook merges that
 *   with route-derived focus. Route wins on field-level conflict — see §2 of
 *   Planning/phase-2c-pre-read.md for rationale.
 *
 * Schema-evolution phase closed the gap for CastBackPage.selectedAlertId (focusedAlertId)
 * and YouTubeIntelPage.expandedContent (focusedContentId) — both are now in PageFocus.
 */
import { useMemo } from 'react'
import { useLocation } from 'react-router'
import type { PageContext } from '../lib/pageContext'
import { usePageFocus } from '../contexts/PageFocusContext'

/** Canonicalize the page slug from the leading path segment.
 *  '/' → 'home'; '/brain/plan/abc' → 'brain'; '/dashboard' → 'dashboard'. */
function deriveCanonicalPage(pathname: string): string {
  return pathname.split('/')[1] || 'home'
}

export function useIvyPageContext(): PageContext {
  const { pathname } = useLocation()
  const { focus } = usePageFocus()

  return useMemo<PageContext>(() => {
    const page = deriveCanonicalPage(pathname)

    // ─── Single canonical route-to-field bridge (Phase 2B) ────────────────
    // Extract :id from pathname directly. useParams() does NOT work here
    // because the hook is called from IvyDock at App root (outside <Routes>).
    // useLocation provides pathname globally inside <BrowserRouter>, so this
    // form is portable — matches route-pattern without needing Route context.
    const brainPlanMatch = pathname.match(/^\/brain\/plan\/([^/]+)/)
    const id = brainPlanMatch?.[1]
    const routeFocus: Partial<PageContext> = id
      ? { focusedPlannedEpisodeId: id }
      : {}

    // ─── State-derived focus sibling bridge (Phase 2C) ────────────────────
    // Pages publish entity focus into PageFocusContext via useRegisterPageFocus.
    // Merge order below puts routeFocus last → route wins on field-level
    // conflict. See Planning/phase-2c-pre-read.md §2 for rationale.
    return { page, ...focus, ...routeFocus }
  }, [pathname, focus])
}
